<template>
  <vetster-input-group
    autocomplete="poop"
    class="font-sm"
    :style="cssProps"
  >
    <div
      :id="geocoderId"
      class="legacy-mapbox w-full"
    />
  </vetster-input-group>
</template>

<script>
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import find from 'lodash/find';
import replace from 'lodash/replace';
import { mapGetters } from 'vuex';

import VetsterInputGroup from '@/components/VetsterInputGroup';
import { uuid } from '@/helpers/uuid';

// TODO: Would like to focus the input box
export default {
  name: 'VetsterCityTypeahead',

  components: { VetsterInputGroup },

  props: {
    filters: {
      type: [Object, Array],
      default: () => ({}),
    },
    locale: {
      type: String,
      default: 'en-US',
    },
    countries: {
      type: String,
      default: 'us',
    },
    size: {
      type: String,
      default: 'base',
    },
    newSearchExperience: {
      type: Boolean,
      default: false,
    },
    geo: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      key: import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN,
      geocoderId: `geocoder-${uuid()}`,
      search_input: undefined,
      inputs: {
        city: '',
        province: '',
        country: '',
      },
    };
  },

  computed: {
    ...mapGetters(['isGlobalUser']),

    cssProps() {
      const cssProperties = {};

      if (this.newSearchExperience) {
        cssProperties['--search-border-radius'] = '8px';
        cssProperties['--search-box-border-radius'] = '10px';
        cssProperties['--search-font-size'] = '16px';
        cssProperties['--search-icon-display'] = 'none';
        cssProperties['--search-padding'] = '0.8rem';
        cssProperties['--search-padding-x'] = '1.2rem';
        cssProperties['--search-min-width'] = '440px';
        cssProperties['--search-icon-margin-close'] = '10px';
      }

      if (this.size === 'xl') {
        cssProperties['--padding'] = '0.9rem';
        cssProperties['--icon-margin'] = '12px';
        cssProperties['--icon-margin-close'] = '14px';
        cssProperties['--icon-top'] = '18px';
        cssProperties['--font-size'] = '1.25rem';
        cssProperties['--font-size-desktop'] = '1.25rem';
        cssProperties['--font-size-suggestions'] = '1rem';
        cssProperties['--font-size-suggestions-desktop'] = '1rem';
        cssProperties['--icon-size'] = '26px';
        cssProperties['--icon-size-close'] = '18px';
        cssProperties['--icon-size-loading'] = '26px';
        cssProperties['--icon-size-search'] = '26px';
      } else {
        cssProperties['--padding'] = '0.5rem';
        cssProperties['--icon-margin'] = '4px';
        cssProperties['--icon-margin-close'] = '3px';
        cssProperties['--icon-margin-loading'] = '0px';
        cssProperties['--icon-top'] = '8px';
        cssProperties['--font-size'] = '14px';
        cssProperties['--font-size-desktop'] = '14px';
        cssProperties['--font-size-suggestions'] = '0.9rem';
        cssProperties['--font-size-suggestions-desktop'] = '0.9rem';
        cssProperties['--icon-size'] = '24px';
        cssProperties['--icon-size-close'] = '16px';
        cssProperties['--icon-size-loading'] = '22px';
        cssProperties['--icon-size-search'] = '22px';
        cssProperties['--search-padding-x'] = '1.75rem';
      }

      return cssProperties;
    },
  },

  beforeDestroy() {
    this.geocoder.off('result', this.updateAddress);
  },

  mounted() {
    this.geocoder = this.geo;

    if (!this.geocoder) {
      this.geocoder = new MapboxGeocoder({
        countries: this.isGlobalUser ? null : this.countries,
        accessToken: this.key,
        language: this.locale,
        placeholder: this.$t('search.start-typing-your-city-name'),
        types: 'place',
        minLength: 3,
        autocomplete: true,
      });
    }

    this.targetSearch();

    this.geocoder.addTo(`#${this.geocoderId}`);
    this.geocoder.on('result', this.updateAddress);
  },

  methods: {
    targetSearch() {
      if (!this.isGlobalUser) {
        this.geocoder.setCountries(this.countries);
      }
    },

    async updateAddress(data) {
      const { result } = data;

      // this.geocoder.setInput(
      //   this.getCity(result) + ', ' + this.getProvince(result)
      // );

      this.inputs = {
        city: this.getCity(result),
        province: this.getProvince(result),
        country: this.getCountry(result),
      };

      await this.$store.commit('search/setInputs', this.inputs);

      this.$emit('selected');
      this.$emit('update', this.inputs);
    },

    getCity(data) {
      // text_fr-ca
      return data?.text;
    },

    getProvince(data) {
      let provinceData = find(data.context, (item) => item.id.includes('region'));

      if (!provinceData?.short_code) {
        provinceData = find(data.context, (item) => item.id.includes('place'));
      }

      // Short code is returned CA-ON for Canada, US-PA for USA, GB-ENG for UK
      return provinceData && replace(provinceData.short_code, /[A-Z]{2}-/g, '');
    },

    getCountry(data) {
      const countryData = find(data.context, (item) => item.id.includes('country'));

      return countryData?.short_code;
    },
  },
};
</script>

<style>
/* ------------------------------------ */
/* purgecss start ignore */
/* ------------------------------------ */
/* Basics */
.legacy-mapbox .mapboxgl-ctrl-geocoder,
.legacy-mapbox .mapboxgl-ctrl-geocoder *,
.legacy-mapbox .mapboxgl-ctrl-geocoder *:after,
.legacy-mapbox .mapboxgl-ctrl-geocoder *:before {
  box-sizing: border-box;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder {
  line-height: 24px;
  position: relative;
  background-color: var(--search-background-color, #ffffff);
  width: 100%;
  min-width: 240px;
  z-index: 1;
  border-radius: 4px;
  transition: width 0.25s, min-width 0.25s;
  border-radius: var(--search-box-border-radius, 0);
}

@media (min-width: 1024px) {
  .legacy-mapbox .mapboxgl-ctrl-geocoder {
    min-width: var(--search-min-width, 240px);
  }
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--input {
  @apply block w-full px-3 transition font-normal;
  font-size: var(--search-font-size, var(--font-size));
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  color: var(--search-color, theme('colors.neutral.900'));
  background-color: var(--search-background-color, #ffffff);
  border-color: var(--search-border-color, #d2d6dc);
  border-width: 1px;
  border-radius: var(--search-border-radius, 0.375rem);
  padding-top: var(--search-padding, var(--padding));
  padding-right: var(--search-padding-x, 0.75rem);
  padding-left: var(--search-padding-x, 2.8rem);
  padding-bottom: var(--search-padding, var(--padding));
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--input::placeholder {
  @apply font-normal;
  color: var(--search-placeholder-color, theme('colors.gray.500'));
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--input::-ms-clear {
  display: none; /* hide input clear button in IE */
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--input:focus {
  @apply outline-none ring;
  --tw-ring-color: var(--search-border-color, theme('colors.indigo.100'));
  border-color: var(--search-border-color, theme('colors.indigo.600'));
  color: var(--search-color, theme('colors.gray.800'));
}

.legacy-mapbox .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--pin-right > * {
  z-index: 2;
  position: absolute;
  right: 8px;
  top: 8px;
  display: none;
}
.legacy-mapbox .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--pin-right:focus,
.legacy-mapbox .mapboxgl-ctrl-geocoder--pin-right:active {
  @apply border-none;
}
/* Collapsed */
.legacy-mapbox .mapboxgl-ctrl-geocoder.mapboxgl-ctrl-geocoder--collapsed {
  width: 50px;
  min-width: 50px;
  transition: width 0.25s, min-width 0.25s;
}

/* Suggestions */
.legacy-mapbox .mapboxgl-ctrl-geocoder .suggestions {
  @apply shadow-xl border border-neutral-100;
  background-color: #fff;
  border-radius: 4px;
  left: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  width: 100%;
  top: 110%; /* fallback */
  top: calc(100% + 6px);
  z-index: 1000;
  overflow: hidden;
  font-size: var(--font-size-suggestions-desktop);
}

.legacy-mapbox .mapboxgl-ctrl-bottom-left .suggestions,
.legacy-mapbox .mapboxgl-ctrl-bottom-right .suggestions {
  top: auto;
  bottom: 100%;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder .suggestions > li > a {
  cursor: default;
  display: block;
  padding: 6px 12px;
  color: #404040;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder .suggestions > .active > a,
.legacy-mapbox .mapboxgl-ctrl-geocoder .suggestions > li > a:hover {
  @apply text-neutral-700 bg-neutral-100;
  text-decoration: none;
  cursor: pointer;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--suggestion-title {
  @apply font-medium;
  font-size: var(--font-size-suggestions-desktop);
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--suggestion-address {
  @apply text-neutral-600 font-light;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--suggestion-title,
.legacy-mapbox .mapboxgl-ctrl-geocoder--suggestion-address {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

/* Icons */
.legacy-mapbox .mapboxgl-ctrl-geocoder--icon {
  display: inline-block;
  vertical-align: middle;
  speak: none;
  fill: #666;
  width: var(--icon-size);
  height: var(--icon-size);
  margin-top: var(--icon-margin);
  margin-right: 3px;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--icon-search {
  position: absolute;
  top: var(--icon-top);
  left: 0px;
  margin: 0;
  width: var(--icon-size-search);
  height: var(--icon-size-search);
  display: var(--search-icon-display, 'inline-block');
}
.legacy-mapbox .mapboxgl-ctrl-geocoder--icon-close {
  margin-top: var(--search-icon-margin-close, var(--icon-margin-close));
  width: var(--icon-size-close);
  height: var(--icon-size-close);
  fill: var(--search-color, #757575);
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--icon-loading {
  margin-top: var(--icon-margin-loading, calc(var(--icon-margin) - 2px));
  width: var(--icon-size-loading);
  height: var(--icon-size-loading);
  -moz-animation: rotate 0.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
  -webkit-animation: rotate 0.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
  animation: rotate 0.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--button {
  padding: 0;
  margin: 0;
  border: none;
  cursor: pointer;
  background: #fff;
  line-height: 1;
  background: var(--search-background-color, #ffffff);
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--button:hover .mapboxgl-ctrl-geocoder--icon-close {
  fill: #909090;
}

.legacy-mapbox .mapboxgl-ctrl-geocoder--powered-by {
  display: none !important;
}

/* Animation */
@-webkit-keyframes rotate {
  from {
    -webkit-transform: rotate(0);
    transform: rotate(0);
  }
  to {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes rotate {
  from {
    -webkit-transform: rotate(0);
    transform: rotate(0);
  }
  to {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

/* Media queries*/
@media screen and (min-width: 640px) {
  .legacy-mapbox .mapboxgl-ctrl-geocoder.mapboxgl-ctrl-geocoder--collapsed {
    width: 36px;
    min-width: 36px;
  }

  .legacy-mapbox .mapboxgl-ctrl-geocoder {
    /*width: 33.3333%;*/
    line-height: 20px;
    /*max-width: 360px;*/
  }
  .legacy-mapbox .mapboxgl-ctrl-geocoder .suggestions {
    font-size: var(--font-size-suggestions-desktop);
  }

  .legacy-mapbox .mapboxgl-ctrl-geocoder--input {
    font-size: var(--search-font-size, var(--font-size-desktop));
  }

  .legacy-mapbox .mapbox-gl-geocoder--error {
    color: #909090;
    padding: 6px 12px;
    font-size: var(--font-size-desktop);
    text-align: center;
  }
}
/* ------------------------------------ */
/* purgecss end ignore */
/* ------------------------------------ */
</style>
